<template>
  <div class="w-full max-w-xl p-6 space-y-8 sm:p-8 bg-white rounded-lg shadow dark:bg-gray-800">
    <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
      Step {{ step }}: {{ stepTitles[step - 1] }}
    </h2>
    <form class="mt-8 space-y-6" @submit.prevent="handleSubmit">

      <!-- Step 1: Personal Information -->
      <div v-if="step === 1">
        <InputField id="username" label="Username" v-model="username" type="text" required :validationMessage="validationErrors.username" />
        <InputField id="email" label="Company Email" v-model="email" type="email" required placeholder="Email" :validationMessage="validationErrors.email" />
        <InputField id="password" label="Password" v-model="password" type="password" required placeholder="••••••••" :validationMessage="validationErrors.password" />

        <InputField id="first_name" label="First Name" v-model="formData.personal.first_name" type="text" required :validationMessage="validationErrors.first_name" />
        <InputField id="last_name" label="Last Name" v-model="formData.personal.last_name" type="text" required :validationMessage="validationErrors.last_name" />
        <InputField id="personal_email" label="Personal Email" v-model="formData.personal.personal_email" type="email" required :validationMessage="validationErrors.personal_email" />
        <InputField id="date_of_birth" label="Date of Birth" v-model="formData.personal.date_of_birth" type="date" required :validationMessage="validationErrors.date_of_birth" />
        <SelectField id="gender" label="Gender" v-model="formData.personal.gender" :options="[{ value: '', text: 'Select gender' }, { value: 'male', text: 'Male' }, { value: 'female', text: 'Female' }]" required :validationMessage="validationErrors.gender" />
        <div class="mb-2">
          <label for="country" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Country</label>
          <multiselect id="country" v-model="formData.personal.country" :options="countryOptions" :searchable="true" :allow-empty="true" placeholder="Select a country" :show-labels="false" />
          <div v-if="validationErrors.country" class="text-red-500 text-sm mt-1">{{ validationErrors.country }}</div>
        </div>
      </div>

      <!-- Step 2: Contact Information -->
      <div v-if="step === 2">
        <TextArea id="emergency_contacts" label="Emergency Contacts" v-model="formData.contact.emergency_contacts" placeholder="Separate by commas" :validationMessage="validationErrors.emergency_contacts" />
        <InputField id="mobile_number" label="Mobile Number" v-model="formData.contact.mobile_number" type="text" required :validationMessage="validationErrors.mobile_number" />
        <InputField id="work_phone_number" label="Work Phone Number" v-model="formData.contact.work_phone_number" type="text" required :validationMessage="validationErrors.work_phone_number" />
        <InputField id="skype_username" label="Skype username / email" v-model="formData.contact.skype_username" type="text" required :validationMessage="validationErrors.skype_username" />
        <InputField id="telegram_nickname" label="Telegram nickname" v-model="formData.contact.telegram_nickname" type="text" required :validationMessage="validationErrors.telegram_nickname" />
        <InputField id="address" label="Address" v-model="formData.contact.address" type="text" required :validationMessage="validationErrors.address" />
      </div>

      <!-- Step 3: Social Information -->
      <div v-if="step === 3">
        <InputField id="facebook_url" label="Facebook URL" v-model="formData.socials.facebook_url" type="text" :validationMessage="validationErrors.facebook_url" />
        <InputField id="linkedin_url" label="LinkedIn URL" v-model="formData.socials.linkedin_url" type="text" :validationMessage="validationErrors.linkedin_url" />
      </div>

      <!-- Step 4: Position and Employment -->
      <div v-if="step === 4">
        <InputField id="position" label="Position" v-model="formData.positions.position" type="text" required placeholder="WordPress Developer, SEO specialist etc." :validationMessage="validationErrors.position" />
        <InputField id="division" label="Division" v-model="formData.positions.division" type="text" required placeholder="Development, SEO, Engineering etc." :validationMessage="validationErrors.division" />
        <InputField id="location" label="Location" v-model="formData.positions.location" type="text" required placeholder="California, USA / Kiev, Ukraine" :validationMessage="validationErrors.location" />
        <InputField id="department" label="Department" v-model="formData.positions.department" type="text" required :validationMessage="validationErrors.department" />

        <label for="manager" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Manager</label>
        <multiselect id="manager" v-model="formData.positions.manager" :options="managerOptions" :multiple="true" track-by="value" label="text" placeholder="Select one or more managers" :close-on-select="false" :clear-on-select="false" :preserve-search="true" :hide-selected="true" :custom-label="customLabel" />
        <div v-if="validationErrors.manager" class="text-red-500 text-sm mt-1">{{ validationErrors.manager }}</div>

      </div>

      <!-- Step 5: Employment Status -->
      <div v-if="step === 5">
        <InputField id="hired_on" label="Hired On" v-model="formData.employment_status.hired_on" type="date" :validationMessage="validationErrors.hired_on" />
        <InputField id="employment_type" label="Employment Type" v-model="formData.employment_status.employment_type" type="text" placeholder="full-time, part-time etc." :validationMessage="validationErrors.employment_type" />
        <InputField id="probation_ends_on" label="Probation Ends On" v-model="formData.employment_status.probation_ends_on" type="date" :validationMessage="validationErrors.probation_ends_on" />
      </div>

      <!-- Step 6: Compensation -->
      <div v-if="step === 6">
        <div v-for="(comp, index) in formData.compensation" :key="index" class="space-y-4">
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">Compensation {{ index + 1 }}</h3>
          <InputField id="effective_from" label="Effective From" v-model="comp.effective_from" type="date"  :validationMessage="validationErrors['compensation.effective_from.' + index]" />
          <InputField id="amount" label="Amount" v-model="comp.amount" type="text"  :validationMessage="validationErrors['compensation.amount.' + index]" />
          <SelectField id="per" label="Per" v-model="comp.per" :options="[{ value: '', text: 'Select' }, { value: 'hour', text: 'Hour' }, { value: 'day', text: 'Day' }, { value: 'week', text: 'Week' }, { value: 'month', text: 'Month' }, { value: 'year', text: 'Year' }]"  :validationMessage="validationErrors['compensation.per.' + index]" />
          <SelectField id="overtime" label="Overtime" v-model="comp.overtime" :options="[{ value: '', text: 'Select' }, { value: 'yes', text: 'Yes' }, { value: 'no', text: 'No' }]"  :validationMessage="validationErrors['compensation.overtime.' + index]" />
          <InputField id="pay_schedule" label="Pay Schedule" v-model="comp.pay_schedule" type="text"  placeholder="Once per month, twice per month etc." :validationMessage="validationErrors['compensation.pay_schedule.' + index]" />
        </div>
        <Button @click="addCompensation" label="Add Compensation" type="button" />
      </div>

      <!-- Navigation Buttons -->
      <div class="flex justify-between">
        <Button v-if="step > 1" @click="prevStep" label="Previous" type="button" />
        <Button v-if="step < 6" @click="nextStep" label="Next" type="button" />
        <Button v-if="step === 6" label="Submit" type="submit" />
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
import InputField from './FormComponents/InputField';
import TextArea from './FormComponents/TextArea';
import Button from './FormComponents/Button';
import SelectField from './FormComponents/SelectField';
import Multiselect from 'vue-multiselect';
import { countries } from '../data/countries';

export default {
  components: {
    InputField,
    TextArea,
    Button,
    SelectField,
    Multiselect,
  },
  mounted() {
    this.fetchManagers();
  },
  data() {
    return {
      step: 1,
      stepTitles: [
        "Personal Information",
        "Contact Information",
        "Social Information",
        "Position and Employment",
        "Employment Status",
        "Compensation"
      ],
      username: '',
      email: '',
      password: '',
      formData: {
        personal: {
          first_name: '',
          last_name: '',
          personal_email: '',
          date_of_birth: '',
          gender: '',
          email: '',
          country: '',
        },
        contact: {
          emergency_contacts: '',
          mobile_number: '',
          work_phone_number: '',
          skype_username: '',
          telegram_nickname: '',
          address: '',
        },
        socials: {
          facebook_url: '',
          linkedin_url: ''
        },
        positions: {
          position: '',
          division: '',
          location: '',
          department: '',
          manager: []
        },
        employment_status: {
          hired_on: '',
          employment_type: '',
          probation_ends_on: ''
        },
        compensation: [
          {
            effective_from: '',
            amount: '',
            per: '',
            overtime: '',
            pay_schedule: ''
          }
        ]
      },
      countryOptions: countries,
      managerOptions: [],
      validationErrors: {}
    };
  },
  methods: {
    customLabel({ text }) {
      return `${text}`;
    },
    async fetchManagers() {
      try {
        const response = await axios.get('/wp-json/custom/v1/managers');
        this.managerOptions = response.data.map(manager => ({
          value: manager.id,
          text: manager.name
        }));
      } catch (error) {
        console.error('Failed to fetch managers:', error);
      }
    },
    nextStep() {
      if (this.validateStep()) {
        this.step++;
      }
    },
    prevStep() {
      this.step--;
    },
    addCompensation() {
      this.formData.compensation.push({
        effective_from: '',
        amount: '',
        per: '',
        overtime: '',
        pay_schedule: ''
      });
    },
    validateStep() {
      this.validationErrors = {};

      if (this.step === 1) {
        if (!this.username) {
          this.validationErrors.username = "Username is required.";
        }
        if (!this.email) {
          this.validationErrors.email = "Email is required.";
        } else if (!this.email.includes('@clixsy.com')) {
          this.validationErrors.email = "Please use a valid company email ending with @clixsy.com.";
        }
        if (!this.password) {
          this.validationErrors.password = "Password is required.";
        }
        if (!this.formData.personal.first_name) {
          this.validationErrors.first_name = "First Name is required.";
        }
        if (!this.formData.personal.last_name) {
          this.validationErrors.last_name = "Last Name is required.";
        }
        if (!this.formData.personal.date_of_birth) {
          this.validationErrors.date_of_birth = "Date of Birth is required.";
        }
        if (!this.formData.personal.gender) {
          this.validationErrors.gender = "Gender is required.";
        }
      }

      return Object.keys(this.validationErrors).length === 0;
    },
    async handleSubmit() {
      if (!this.validateStep()) {
        return;
      }

      const payload = {
        username: this.username,
        email: this.email,
        password: this.password,
        formData: this.formData
      };
      console.log('formData = ', this.formData);

      try {
        const response = await axios.post('/wp-json/custom/v1/register', payload);
        console.log('Registration successful:', response.data);

        // Start the confetti animation
        this.$confetti.start();

        // Stop confetti and redirect after 3 seconds
        setTimeout(() => {
          this.$confetti.stop();
          window.location.href = '/login';
        }, 3000);

      } catch (error) {
        console.error('Registration failed:', error.response ? error.response.data : error.message);
        this.validationErrors.general = 'Registration failed: ' + (error.response ? error.response.data.message : error.message);
      }
    }

  }
};
</script>


<style scoped>
/* Add your styles here */
</style>
